import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Upload,
  FileText,
  Image,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Table2,
} from 'lucide-react';

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.doc', '.xlsx', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
const MAX_SIZE = 25 * 1024 * 1024;

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(name) {
  const ext = (name || '').split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(ext)) return Image;
  if (['xlsx', 'xls'].includes(ext)) return Table2;
  return FileText;
}

export default function DocumentUpload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetLocation = searchParams.get('location');
  const { authAxios } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const addFiles = useCallback((newFiles) => {
    const valid = [];
    for (const file of newFiles) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`${file.name}: ongeldig bestandstype`);
        continue;
      }
      if (file.size > MAX_SIZE) {
        toast.error(`${file.name}: te groot (max 25MB)`);
        continue;
      }
      valid.push(file);
    }
    setFiles(prev => [...prev, ...valid]);
  }, []);

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleUploadAndExtract = async () => {
    if (!files.length) return;
    setUploading(true);

    try {
      const api = authAxios();
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));

      const uploadRes = await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const uploads = uploadRes.data.uploads || [];
      const errors = uploads.filter(u => u.error);
      const success = uploads.filter(u => !u.error);

      if (errors.length) {
        errors.forEach(e => toast.error(`${e.filename}: ${e.error}`));
      }

      if (!success.length) {
        toast.error('Geen bestanden succesvol ge\u00fcpload');
        setUploading(false);
        return;
      }

      // Extract each uploaded document (images now also get AI vision analysis)
      const extractResults = [];
      for (const upload of success) {
        try {
          const extractRes = await api.post(`/documents/${upload.upload_id}/extract`);
          extractResults.push({ ...upload, ...extractRes.data });
        } catch (e) {
          extractResults.push({ ...upload, extraction_status: 'failed', error: 'Extractie mislukt' });
        }
      }

      // Navigate to first successful extraction for review
      const completed = extractResults.find(r => r.extraction_status === 'completed');

      const locQuery = presetLocation ? `?location=${presetLocation}` : '';
      if (completed) {
        toast.success(`${success.length} document(en) ge\u00fcpload en geanalyseerd`);
        navigate(`/kennisbank/document/${completed.upload_id}${locQuery}`);
      } else {
        toast.error('AI extractie mislukt voor alle documenten. Probeer handmatig.');
        if (success[0]) {
          navigate(`/kennisbank/document/${success[0].upload_id}${locQuery}`);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Fout bij uploaden');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-[800px]">
      <button
        onClick={() => navigate(presetLocation ? `/locatie/${presetLocation}` : '/kennisbank')}
        data-testid="back-to-kennisbank"
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#2E75B6] mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {presetLocation ? 'Terug naar locatie' : 'Terug naar Kennisbank'}
      </button>

      <div className="space-y-4">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-[#E6EDF3]">Document uploaden</h2>
          <p className="text-xs text-slate-500 dark:text-[#8B949E] mt-1">Upload PDF, DOCX, Excel of afbeeldingen (incl. iPhone HEIC). AI analyseert de inhoud en stelt metadata voor.</p>
        </div>

        {/* Drop zone */}
        <div
          data-testid="document-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors duration-200 ${
            dragActive
              ? 'border-[#2E75B6] bg-[#2E75B6]/5'
              : 'border-slate-200 dark:border-[#30363D] hover:border-slate-300 dark:hover:border-[#484F58] bg-white dark:bg-[#161B22]'
          }`}
        >
          <Upload className={`w-8 h-8 mx-auto mb-3 ${dragActive ? 'text-[#2E75B6]' : 'text-slate-300'}`} />
          <p className="text-sm text-slate-600 mb-1">Sleep bestanden hierheen of</p>
          <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] cursor-pointer transition-colors">
            Bestanden kiezen
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.xlsx,.jpg,.jpeg,.png,.webp,.heic,.heif"
              onChange={(e) => { if (e.target.files?.length) addFiles(Array.from(e.target.files)); e.target.value = ''; }}
              className="hidden"
              data-testid="document-file-input"
            />
          </label>
          <p className="text-[10px] text-slate-400 mt-3">PDF, DOCX, XLSX, JPG, PNG, WEBP, HEIC — max 25MB per bestand</p>
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200/80 divide-y divide-slate-100" data-testid="document-file-list">
            {files.map((file, i) => {
              const Icon = getFileIcon(file.name);
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{file.name}</p>
                    <p className="text-[10px] text-slate-400">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    onClick={() => removeFile(i)}
                    data-testid={`remove-file-${i}`}
                    className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Upload button */}
        {files.length > 0 && (
          <button
            onClick={handleUploadAndExtract}
            disabled={uploading}
            data-testid="upload-and-analyze-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] active:scale-[0.99] transition-all disabled:opacity-60"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Document(en) uploaden & analyseren...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload & Analyseer ({files.length} bestand{files.length !== 1 ? 'en' : ''})
              </>
            )}
          </button>
        )}

        {/* Info cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#30363D] p-4">
            <FileText className="w-5 h-5 text-[#2E75B6] mb-2" />
            <h4 className="text-xs font-semibold text-slate-700 dark:text-[#C9D1D9] mb-1">PDF & DOCX</h4>
            <p className="text-[10px] text-slate-400 dark:text-[#484F58]">Tekst wordt automatisch ge\u00eaxtraheerd en geanalyseerd door AI.</p>
          </div>
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#30363D] p-4">
            <Table2 className="w-5 h-5 text-emerald-500 mb-2" />
            <h4 className="text-xs font-semibold text-slate-700 dark:text-[#C9D1D9] mb-1">Excel (.xlsx)</h4>
            <p className="text-[10px] text-slate-400 dark:text-[#484F58]">Werkbladen worden uitgelezen en door AI geanalyseerd.</p>
          </div>
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#30363D] p-4">
            <Image className="w-5 h-5 text-violet-500 mb-2" />
            <h4 className="text-xs font-semibold text-slate-700 dark:text-[#C9D1D9] mb-1">Afbeeldingen</h4>
            <p className="text-[10px] text-slate-400 dark:text-[#484F58]">AI analyseert foto's, scans en bouwtekeningen met beeldherkenning.</p>
          </div>
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#30363D] p-4">
            <CheckCircle2 className="w-5 h-5 text-amber-500 mb-2" />
            <h4 className="text-xs font-semibold text-slate-700 dark:text-[#C9D1D9] mb-1">Review & Bevestig</h4>
            <p className="text-[10px] text-slate-400 dark:text-[#484F58]">Controleer AI suggesties, pas aan en sla op als vault item.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
